import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import {
  Lead,
  LeadFollowUp,
  LeadFollowUpSummary,
  LeadListResponse,
  LeadOption,
  RecordFollowUpResultPayload,
  ToLeadResponse,
} from './model'

export const LeadService = (client: AxiosInstance) => {
  return {
    async getLeads(opt: PaginationOpt): Promise<LeadListResponse> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination: Record<string, unknown>
          data: Record<string, unknown>[]
          followUpSummary?: LeadFollowUpSummary
        }>('/lead', { params: opt, paramsSerializer: { indexes: null } })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) => deserialize(item, Lead)) || [],
        followUpSummary: data?.followUpSummary,
      }
    },
    async getInCompletedLeads(opt: PaginationOpt): Promise<PaginationResponse<Lead>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          '/lead/incomplete',
          { params: opt }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) => deserialize(item, Lead)) || [],
      }
    },
    async getLeadStatus(): Promise<LeadOption[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>('/lead/status')
      )
      return data.map((item: Record<string, unknown>) => deserialize(item, LeadOption))
    },
    async createLead(lead: Lead): Promise<void> {
      await wrapApiCall(() => client.post('/leads', lead.serializeToCreate()))
    },
    async getLeadById(id: string | number): Promise<Lead> {
      const { data } = await wrapApiCall(() => client.get<Record<string, unknown>>(`/leads/${id}`))
      return deserialize(data, Lead)
    },
    async getLeadFollowUpById(id: string | number): Promise<LeadFollowUp[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/leads/${id}/follow-up`)
      )
      return data.map((item: Record<string, unknown>) => deserialize(item, LeadFollowUp)) || []
    },
    async updateLeadProfile(id: string | number, lead: Lead): Promise<void> {
      await wrapApiCall(() => client.patch(`/leads/${id}/profile`, lead.serializeToUpdateProfile()))
    },
    async updateLeadContact(id: string | number, lead: Lead): Promise<void> {
      await wrapApiCall(() => client.patch(`/leads/${id}/contact`, lead.serializeToUpdateContact()))
    },
    async updateLeadReferral(id: string | number, lead: Lead): Promise<void> {
      await wrapApiCall(() =>
        client.patch(`/leads/${id}/referral`, lead.serializeToUpdateReferral())
      )
    },
    async updateLeadPackage(id: string | number, lead: Lead): Promise<void> {
      await wrapApiCall(() =>
        client.patch(`/leads/${id}/package-interest`, lead.serializeToUpdatePackage())
      )
    },
    async updateLeadNote(id: string | number, note: string): Promise<void> {
      await wrapApiCall(() => client.patch(`/leads/${id}/note`, { leadNote: note }))
    },
    async updateLeadStatus(id: string | number, lead: Lead): Promise<ToLeadResponse> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success: boolean }>(`/leads/${id}/status`, lead.serializeToUpdateStatus())
      )
      return { success: data.success, toStatus: lead.status }
    },
    async createFollowUpResult(id: string | number, followUp: LeadFollowUp): Promise<void> {
      await wrapApiCall(() => client.post(`/leads/${id}/follow-up/`, followUp.serializeToCreate()))
    },
    async saveFollowUpResult(
      id: string | number,
      followUpId: string | number,
      payload: RecordFollowUpResultPayload
    ): Promise<void> {
      await wrapApiCall(() => client.patch(`/leads/${id}/follow-up/${followUpId}/result`, payload))
    },
    async revokeLeadReferral(id: string | number): Promise<void> {
      await wrapApiCall(() => client.delete(`/leads/${id}/referral`))
    },
    async updateLead(id: string | number, lead: Lead): Promise<ToLeadResponse> {
      await this.updateLeadProfile(id, lead)
      await this.updateLeadContact(id, lead)
      await this.updateLeadReferral(id, lead)
      await this.updateLeadPackage(id, lead)
      await this.updateLeadStatus(id, lead)
      return { success: true, toStatus: lead.status }
    },
  }
}
